import usePageTitle from '@/hooks/use-page-title';
import { Link } from "react-router-dom";
import { ChevronRight, Copyright, MessageSquareWarning, ExternalLink } from 'lucide-react';
import AprilPicto from '@/components/shared/april-picto';
import { PictoEmmenagement, PictoConformite, PictoEspaceAssure } from '@/assets/icons-april-fr/pictos';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import useBrandName from '@/hooks/use-brand-name';

const Mentions = () => {
    usePageTitle('Mentions légales');
    const brandName = useBrandName();
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="space-y-2 sm:space-y-3 mt-4">
                <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground" aria-label="Fil d'Ariane">
                    <Link to="/" className="hover:text-foreground transition-colors no-underline">Mon espace</Link>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span>Mentions légales</span>
                </nav>
                <h1 className="text-xl sm:text-2xl font-bold">Mentions légales</h1>
            </div>

            {/* Nom de la marque */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoEmmenagement} size="md" />
                        {brandName}
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Société de courtage d'assurances régie par le Code des Assurances
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Société Anonyme Simplifiée au capital de 300 000 €
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Intermédiaire en assurances RCS Tours B 397 855 867
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Immatriculée à l'ORIAS sous le n° 07 008 730 – www.orias.fr
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Une société du Groupe APRIL
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            Entreprise soumise au contrôle de l'Autorité de Contrôle Prudentiel et de Résolutions (ACPR) – 4 Place de Budapest CS 92459, 75436 Paris
                        </li>
                    </ul>
                    <p className="text-sm text-muted-foreground">
                        Nos garanties Responsabilité Civile Professionnelle et Garantie Financière sont conformes aux articles L512-14 et L512-7 du Code des Assurances.
                    </p>
                </CardContent>
            </Card>

            {/* Copyright */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Copyright className="w-5 h-5 text-primary" />
                        Copyright
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                        Tous droits du propriétaire du contenu du site internet www.april-moto.com réservés. Toute reproduction et toute utilisation du contenu du site autre que la consultation individuelle et privée sont interdites, sauf autorisation écrite de notre part. Les logos, marques et marques déposées sont la propriété de APRIL MOTO.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-0.5">Directeur de la publication</p>
                            <p className="text-sm font-medium">Fanny LALUC-FAYET</p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-0.5">Conception</p>
                            <p className="text-sm font-medium">APRIL MOTO</p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-0.5">Hébergement</p>
                            <p className="text-sm font-medium">
                                <a href="https://www.cloud-temple.com/" rel="noreferrer" target="_blank" className="text-primary hover:underline inline-flex items-center gap-1">
                                    CLOUD TEMPLE <ExternalLink className="w-3 h-3" />
                                </a>
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">31 Rue de la Milletière, 37100 Tours – France</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Loi Applicable */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoConformite} size="md" />
                        Loi Applicable
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <p className="text-sm">
                        Les relations précontractuelles et contractuelles sont régies par la loi française et principalement le Code des Assurances.
                    </p>
                </CardContent>
            </Card>

            {/* Réclamations */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <MessageSquareWarning className="w-5 h-5 text-primary" />
                        Réclamations
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <p className="text-sm">
                        Pour toutes réclamations vous pouvez consulter notre page dédiée :
                    </p>
                    <Button asChild variant="outline" size="sm" className="mt-4 cursor-pointer">
                        <a href="https://www.april-moto.com/reclamations" rel="noreferrer" target="_blank">
                            <ExternalLink className="w-4 h-4" />
                            Page réclamations
                        </a>
                    </Button>
                </CardContent>
            </Card>

            {/* Index d'égalité professionnelle */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoEspaceAssure} size="md" />
                        Index d'égalité professionnelle
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                        L'index de l'égalité professionnelle a été instauré par la Loi pour la liberté de choisir son avenir professionnel. Cet index permet aux entreprises de plus de 50 salariés d'évaluer sur 100 points le niveau d'égalité entre les hommes et les femmes.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground mb-1">Ecart de rémunération</p>
                            <p className="text-lg font-bold text-primary">35<span className="text-sm font-normal text-muted-foreground">/40</span></p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground mb-1">Ecart d'augmentations</p>
                            <p className="text-lg font-bold text-primary">35<span className="text-sm font-normal text-muted-foreground">/35</span></p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground mb-1">Retour congé maternité</p>
                            <p className="text-lg font-bold text-primary">15<span className="text-sm font-normal text-muted-foreground">/15</span></p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground mb-1">10 plus hautes rém.</p>
                            <p className="text-lg font-bold text-primary">5<span className="text-sm font-normal text-muted-foreground">/10</span></p>
                        </div>
                    </div>
                    <div className="flex items-center justify-center p-6 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-base font-semibold">
                            Résultat de l'index : <span className="text-primary text-xl">90<span className="text-sm">/100</span></span>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Mentions;
